import { motion } from 'framer-motion'
import { last } from 'lodash'
import React, { useMemo } from 'react'
import { Column, Row, Text } from '@/components/core'
import { classNameBuilder } from '@/helpers'

export interface PauseMetricsProps {
  title: string
  label: string
  currentValue?: number
  goodValue: number
  goodValueType: 'until' | 'more'
  markers: { limit: number; variant: 'good' | 'warning' | 'bad' }[]
  isResponsive?: boolean
}
export const SpeakingMetrics: React.FC<PauseMetricsProps> = ({
  title,
  label,
  currentValue = 0,
  goodValue,
  goodValueType,
  markers,
  isResponsive = true
}) => {
  const bars = useMemo(() => {
    return markers.map(({ limit, variant }, index) => ({
      from: index ? markers[index - 1].limit + 1 : 0,
      to: limit,
      width: (limit / (last(markers)?.limit || 1)) * 100,
      variant
    }))
  }, [markers])

  return (
    <Row
      className={classNameBuilder(
        'h-[140px] w-full flex-1 justify-between border border-gray-50 bg-white px-4',
        isResponsive &&
          'items-center max-lg:h-auto max-lg:flex-col max-lg:space-y-4 max-lg:border-0 max-lg:py-4 lg:rounded-2xl lg:px-8',
        !isResponsive &&
          'h-auto flex-col items-start space-y-4 rounded-2xl px-8 py-4'
      )}
    >
      <Text.Translated
        translationKey={title}
        size="sm"
        fontWeight="bold"
        color="600"
        className={classNameBuilder(
          isResponsive && 'max-lg:text-center',
          !isResponsive && 'text-left'
        )}
      />
      <Column className="h-[100px] w-full justify-end pb-[30px]">
        <Row className="relative h-2 w-full min-w-[186px] shrink-0">
          {bars.map((bar, index) => (
            <motion.div
              key={bar.variant}
              animate={{
                width: `${bar.width}%`
              }}
              className={classNameBuilder(
                'absolute left-0 h-2 rounded-l-2xl',
                {
                  good: 'bg-[#15B9A6]',
                  warning: 'bg-[#F99603]',
                  bad: 'bg-[#CD0101]'
                }[bar.variant],
                { 0: 'z-30', 1: 'z-20', 2: 'z-10' }[index],
                index + 1 === bars.length ? 'rounded-r-2xl' : ''
              )}
            />
          ))}
          {/* Value marker */}
          <motion.div
            className="absolute top-[-4px] z-50 h-4 w-1 rounded-full bg-electric-indigo-500 ring-2 ring-electric-indigo-100/[.30]"
            animate={{
              left: `calc(${
                (currentValue / (last(bars)?.to || 1)) * 100
              }% - 2px)`
            }}
          />
          <motion.div
            className="absolute top-[-58px] flex w-[70px] flex-col items-center justify-center rounded bg-electric-indigo-50"
            animate={{
              left: `calc(${
                (currentValue / (last(bars)?.to || 1)) * 100
              }% - 35px)`
            }}
          >
            <Text
              fontWeight="bold"
              className="mb-[-12px] !text-[12px] !text-electric-indigo-600"
            >
              {currentValue}
            </Text>
            <Text.Translated
              translationKey={label}
              className="!text-[10px] !text-electric-indigo-600"
            />
            <svg
              className="absolute bottom-[-11px] h-3 w-3 fill-electric-indigo-50"
              x="0px"
              y="0px"
              viewBox="0 0 255 255"
            >
              <polygon points="0,0 127.5,127.5 255,0" />
            </svg>
          </motion.div>
          {/* Good Marker */}
          <motion.div
            className="absolute top-[-4px] z-40 h-4 w-1 bg-white"
            animate={{
              left: `calc(${(goodValue / (last(bars)?.to || 1)) * 100}% - 2px)`
            }}
          />
          <motion.div
            className="absolute top-[16px] flex h-[20px] w-[90px] flex-col items-center justify-center rounded bg-[#ecf8f6]"
            animate={{
              left: `calc(${(goodValue / (last(bars)?.to || 1)) * 100}% - 45px)`
            }}
          >
            <Text.Translated
              fontWeight="bold"
              transform="uppercase"
              size="8"
              className="!text-[#15B9A6]"
              translationKey={
                goodValueType === 'until'
                  ? 'until_speaking_metrics'
                  : 'more_speaking_metrics'
              }
            />
            <svg
              className="absolute top-[-7px] h-2 w-2 rotate-180 fill-[#ecf8f6]"
              x="0px"
              y="0px"
              viewBox="0 0 255 255"
            >
              <polygon points="127.5,127.5 0,0 255,0" />
            </svg>
          </motion.div>
        </Row>
      </Column>
    </Row>
  )
}

export default SpeakingMetrics
